import React, { useState, useEffect } from 'react';
import {
    DocumentTextIcon,
    ChartBarIcon,
    GlobeAltIcon,
    CurrencyDollarIcon,
    ArrowTrendingUpIcon,
    BuildingOfficeIcon,
    CalendarDaysIcon,
    ArrowDownTrayIcon,
    Cog6ToothIcon,
    PresentationChartBarIcon,
    DocumentArrowDownIcon,
    ClockIcon
} from '@heroicons/react/24/outline';
import { authorizedFetch } from '../utils/api';

const Reports = () => {
    const [activeTab, setActiveTab] = useState('executive');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [renderTime, setRenderTime] = useState(null);

    // State for different report sections
    const [executiveData, setExecutiveData] = useState({});
    const [performanceData, setPerformanceData] = useState({});
    const [marketData, setMarketData] = useState({});
    const [vendorData, setVendorData] = useState({});
    const [customReports, setCustomReports] = useState([]);
    const [scheduledReports, setScheduledReports] = useState([]);

    const backendUrl = process.env.REACT_APP_BACKEND2_BASE_URL || 'https://suanfamama-fashion-buyer-production.up.railway.app';

    useEffect(() => {
        const startTime = performance.now();
        fetchReportsData().finally(() => {
            const endTime = performance.now();
            setRenderTime(Math.round(endTime - startTime));
        });
    }, []);

    const fetchReportsData = async () => {
        setLoading(true);
        try {
            const [executiveResp, performanceResp, marketResp, vendorResp, customResp] = await Promise.all([
                authorizedFetch(`${backendUrl}/reports/executive`),
                authorizedFetch(`${backendUrl}/reports/performance`),
                authorizedFetch(`${backendUrl}/reports/market-intelligence`),
                authorizedFetch(`${backendUrl}/reports/vendor-analytics`),
                authorizedFetch(`${backendUrl}/reports/custom`)
            ]);

            setExecutiveData(executiveResp.data || {});
            setPerformanceData(performanceResp.data || {});
            setMarketData(marketResp.data || {});
            setVendorData(vendorResp.data || {});
            setCustomReports(customResp.data || []);
        } catch (err) {
            console.error('Error fetching reports data:', err);
            setError('Failed to load reports data');
        } finally {
            setLoading(false);
        }
    };

    const exportReport = async (reportType, format) => {
        try {
            const response = await authorizedFetch(`${backendUrl}/reports/export`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    report_type: reportType,
                    format: format,
                    timestamp: new Date().toISOString()
                })
            });

            if (response.download_url) {
                window.open(response.download_url, '_blank');
            }
        } catch (err) {
            console.error('Error exporting report:', err);
        }
    };

    const LoadingSkeleton = () => (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[1, 2, 3, 4].map(i => (
                    <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                        <div className="animate-pulse">
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
                            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const ExecutiveView = () => (
        <div className="space-y-6">
            {/* Executive KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg">
                    <div className="flex items-center">
                        <CurrencyDollarIcon className="h-10 w-10 text-blue-600 dark:text-blue-400 mr-4" />
                        <div>
                            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">$12.8M</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Total Revenue Impact</div>
                            <div className="text-xs text-green-600 dark:text-green-400 mt-1">↗ +15.3% YoY</div>
                        </div>
                    </div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg">
                    <div className="flex items-center">
                        <ArrowTrendingUpIcon className="h-10 w-10 text-green-600 dark:text-green-400 mr-4" />
                        <div>
                            <div className="text-3xl font-bold text-green-600 dark:text-green-400">87.2%</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Buying Accuracy</div>
                            <div className="text-xs text-green-600 dark:text-green-400 mt-1">↗ +5.8% vs Target</div>
                        </div>
                    </div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-lg">
                    <div className="flex items-center">
                        <GlobeAltIcon className="h-10 w-10 text-purple-600 dark:text-purple-400 mr-4" />
                        <div>
                            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">24</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Global Markets</div>
                            <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">3 New Markets</div>
                        </div>
                    </div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-lg">
                    <div className="flex items-center">
                        <BuildingOfficeIcon className="h-10 w-10 text-yellow-600 dark:text-yellow-400 mr-4" />
                        <div>
                            <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">156</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Active Vendors</div>
                            <div className="text-xs text-green-600 dark:text-green-400 mt-1">92% Performance</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Regional Performance */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Global Regional Performance - Q1 2024
                        </h3>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => exportReport('executive', 'pdf')}
                                className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center text-sm"
                            >
                                <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                                PDF
                            </button>
                            <button
                                onClick={() => exportReport('executive', 'excel')}
                                className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center text-sm"
                            >
                                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                                Excel
                            </button>
                        </div>
                    </div>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            {
                                region: "EMEA",
                                revenue: "$4.8M",
                                growth: "+18.2%",
                                margin: "42.1%",
                                topBrands: ["Ganni", "Acne Studios", "COS"],
                                performance: "Excellent",
                                color: "green"
                            },
                            {
                                region: "Americas",
                                revenue: "$4.2M",
                                growth: "+12.7%",
                                margin: "38.9%",
                                topBrands: ["& Other Stories", "Arket", "Mango"],
                                performance: "Good",
                                color: "blue"
                            },
                            {
                                region: "APAC",
                                revenue: "$3.8M",
                                growth: "+24.1%",
                                margin: "35.6%",
                                topBrands: ["COS", "Massimo Dutti", "Zara"],
                                performance: "Excellent",
                                color: "purple"
                            }
                        ].map((region, index) => (
                            <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                <div className="flex justify-between items-center mb-3">
                                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        {region.region}
                                    </h4>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${region.performance === 'Excellent'
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                                        }`}>
                                        {region.performance}
                                    </span>
                                </div>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Revenue:</span>
                                        <span className="font-medium text-gray-900 dark:text-white">{region.revenue}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Growth:</span>
                                        <span className="font-medium text-green-600 dark:text-green-400">{region.growth}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Margin:</span>
                                        <span className="font-medium text-gray-900 dark:text-white">{region.margin}</span>
                                    </div>
                                    <div className="mt-3">
                                        <span className="text-gray-600 dark:text-gray-400 text-xs">Top Brands:</span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {region.topBrands.map((brand, i) => (
                                                <span key={i} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs">
                                                    {brand}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Key Insights */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Executive Key Insights & Recommendations
                    </h3>
                </div>
                <div className="p-6">
                    <div className="space-y-4">
                        {[
                            {
                                type: "Opportunity",
                                title: "APAC Market Expansion",
                                description: "APAC showing strongest growth at +24.1%. Recommend increasing budget allocation by 15% for Q2.",
                                impact: "High",
                                timeline: "Q2 2024"
                            },
                            {
                                type: "Risk",
                                title: "EMEA Margin Pressure",
                                description: "Currency fluctuations impacting EMEA margins. Consider hedging strategies for major EUR purchases.",
                                impact: "Medium",
                                timeline: "Immediate"
                            },
                            {
                                type: "Success",
                                title: "Sustainable Fashion Performance",
                                description: "Sustainable brands showing 28% higher sell-through rates. Expand sustainable portfolio.",
                                impact: "High",
                                timeline: "Q3 2024"
                            },
                            {
                                type: "Trend",
                                title: "Digital-First Brands Rising",
                                description: "Digital-native brands outperforming traditional brands by 22%. Prioritize digital partnerships.",
                                impact: "Medium",
                                timeline: "Ongoing"
                            }
                        ].map((insight, index) => (
                            <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className={`px-2 py-1 rounded-full text-xs font-medium mr-3 mt-1 ${insight.type === 'Opportunity' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                                        insight.type === 'Risk' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                                            insight.type === 'Success' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                                                'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
                                        }`}>
                                        {insight.type}
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                                            {insight.title}
                                        </h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                            {insight.description}
                                        </p>
                                        <div className="flex justify-between items-center text-xs">
                                            <span className={`font-medium ${insight.impact === 'High' ? 'text-red-600 dark:text-red-400' :
                                                insight.impact === 'Medium' ? 'text-yellow-600 dark:text-yellow-400' :
                                                    'text-green-600 dark:text-green-400'
                                                }`}>
                                                {insight.impact} Impact
                                            </span>
                                            <span className="text-gray-500 dark:text-gray-400">
                                                Timeline: {insight.timeline}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    const PerformanceView = () => (
        <div className="space-y-6">
            {/* Performance Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">94.2%</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Forecast Accuracy</div>
                    <div className="text-xs text-green-600 dark:text-green-400 mt-1">↗ +3.1% vs LY</div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">78.5%</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Sell-Through Rate</div>
                    <div className="text-xs text-green-600 dark:text-green-400 mt-1">↗ +5.2% vs Target</div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">2.1x</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Inventory Turnover</div>
                    <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">Industry Leading</div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">41.8%</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Gross Margin</div>
                    <div className="text-xs text-green-600 dark:text-green-400 mt-1">↗ +2.3% vs LY</div>
                </div>
            </div>

            {/* Category Performance */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Category Performance Analysis
                        </h3>
                        <button
                            onClick={() => exportReport('performance', 'excel')}
                            className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center text-sm"
                        >
                            <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                            Export Data
                        </button>
                    </div>
                </div>
                <div className="p-6">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-gray-700">
                                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Category</th>
                                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Revenue</th>
                                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Growth</th>
                                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Margin</th>
                                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Sell-Through</th>
                                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Performance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    { category: "Dresses", revenue: "$2.8M", growth: "+22.1%", margin: "45.2%", sellThrough: "82.3%", performance: "Excellent" },
                                    { category: "Outerwear", revenue: "$2.1M", growth: "+18.7%", margin: "38.9%", sellThrough: "76.8%", performance: "Good" },
                                    { category: "Tops", revenue: "$1.9M", growth: "+15.3%", margin: "42.1%", sellThrough: "79.2%", performance: "Good" },
                                    { category: "Bottoms", revenue: "$1.6M", growth: "+12.8%", margin: "39.7%", sellThrough: "74.5%", performance: "Average" },
                                    { category: "Accessories", revenue: "$1.4M", growth: "+28.9%", margin: "48.3%", sellThrough: "85.1%", performance: "Excellent" },
                                    { category: "Footwear", revenue: "$1.2M", growth: "+16.4%", margin: "35.8%", sellThrough: "71.9%", performance: "Good" },
                                    { category: "Knitwear", revenue: "$1.0M", growth: "+19.2%", margin: "41.6%", sellThrough: "77.3%", performance: "Good" },
                                    { category: "Bags", revenue: "$0.8M", growth: "+31.5%", margin: "52.1%", sellThrough: "88.7%", performance: "Excellent" }
                                ].map((item, index) => (
                                    <tr key={index} className="border-b border-gray-100 dark:border-gray-700">
                                        <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{item.category}</td>
                                        <td className="py-3 px-4 text-gray-900 dark:text-white">{item.revenue}</td>
                                        <td className="py-3 px-4 text-green-600 dark:text-green-400">{item.growth}</td>
                                        <td className="py-3 px-4 text-gray-900 dark:text-white">{item.margin}</td>
                                        <td className="py-3 px-4 text-gray-900 dark:text-white">{item.sellThrough}</td>
                                        <td className="py-3 px-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.performance === 'Excellent' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                                                item.performance === 'Good' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                                                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                                                }`}>
                                                {item.performance}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );

    const MarketView = () => (
        <div className="space-y-6">
            {/* Market Intelligence Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">47</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Tracked Competitors</div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">89%</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Market Coverage</div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">156</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Trend Signals</div>
                </div>
            </div>

            {/* Competitive Analysis */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Competitive Intelligence Dashboard
                    </h3>
                </div>
                <div className="p-6">
                    <div className="text-center py-8">
                        <GlobeAltIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            Global Market Intelligence
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                            Comprehensive competitive analysis and market intelligence reports coming soon.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const CustomReportsView = () => (
        <div className="space-y-6">
            {/* Report Builder */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Custom Report Builder
                    </h3>
                </div>
                <div className="p-6">
                    <div className="text-center py-8">
                        <Cog6ToothIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            Custom Report Builder
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                            Build custom reports tailored to your specific needs and stakeholders.
                        </p>
                    </div>
                </div>
            </div>

            {/* Scheduled Reports */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Scheduled Reports
                    </h3>
                </div>
                <div className="p-6">
                    <div className="text-center py-8">
                        <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            Automated Reporting
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                            Schedule regular reports for automatic delivery to your global teams.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const tabs = [
        { id: 'executive', label: 'Executive Dashboard', icon: PresentationChartBarIcon },
        { id: 'performance', label: 'Performance Analytics', icon: ChartBarIcon },
        { id: 'market', label: 'Market Intelligence', icon: GlobeAltIcon },
        { id: 'vendor', label: 'Vendor Analytics', icon: BuildingOfficeIcon },
        { id: 'custom', label: 'Custom Reports', icon: DocumentTextIcon }
    ];

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                        Professional Reports & Analytics
                    </h1>
                    <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                    </div>
                </div>
                <LoadingSkeleton />
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-8">
                <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
                <button
                    onClick={fetchReportsData}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                    Professional Reports & Analytics
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                    Comprehensive reporting suite for worldwide professional fashion buyers.
                    Executive dashboards, performance analytics, market intelligence, and custom reporting tools.
                </p>
            </div>

            {/* Tab Navigation */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="border-b border-gray-200 dark:border-gray-700">
                    <nav className="flex space-x-8 px-6">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                        }`}
                                >
                                    <Icon className="h-5 w-5 mr-2" />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </nav>
                </div>

                <div className="p-6">
                    {activeTab === 'executive' && <ExecutiveView />}
                    {activeTab === 'performance' && <PerformanceView />}
                    {activeTab === 'market' && <MarketView />}
                    {activeTab === 'vendor' && (
                        <div className="text-center py-8">
                            <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                Vendor Analytics
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">
                                Comprehensive vendor performance analytics and scorecards coming soon.
                            </p>
                        </div>
                    )}
                    {activeTab === 'custom' && <CustomReportsView />}
                </div>
            </div>

            {/* Performance Info */}
            {renderTime && (
                <div className="text-center text-xs text-gray-500 dark:text-gray-400">
                    Loaded in {renderTime}ms
                </div>
            )}
        </div>
    );
};

export default Reports;
